import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import { OptionDataContenxt } from "../OptionChartDashboard";
import OptionTVChart from "../OptionTVChart";

const DEFAULT_OPTIONS_SLOTS = [
  {
    id: "slot_0",
    currencycode: "BTC",
    basecurrency: "USDT",
    assetpair: "BTC-261023-83000-C",
    assetPairName: "BTC 261023 83000 C",
  },
  {
    id: "slot_1",
    currencycode: "BTC",
    basecurrency: "USDT",
    assetpair: "BTC-261023-83000-P",
    assetPairName: "BTC 261023 83000 P",
  },
  {
    id: "slot_2",
    currencycode: "ETH",
    basecurrency: "USDT",
    assetpair: "ETH-261023-3000-C",
    assetPairName: "ETH 261023 3000 C",
  },
  {
    id: "slot_3",
    currencycode: "ETH",
    basecurrency: "USDT",
    assetpair: "ETH-261023-3000-P",
    assetPairName: "ETH 261023 3000 P",
  },
];

export const FIXED_CHART_CONTAINER_HEIGHT = "470px";

const OptionMultiChartContainer = ({
  layout = "1",
  onLayoutChange,
  btnColor: propBtnColor,
  isFullscreen = false,
}) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const {
    currencycode,
    basecurrency,
    assetpair,
    assetPairName,
    setCurrencycode,
    setBasecurrency,
    setAssetPair,
    setAssetPairName,
  } = useContext(OptionDataContenxt) || {};

  const [activeSlotIndex, setActiveSlotIndex] = useState(0);
  const isInternalChangeRef = useRef(false);

  // Trigger window resize so TradingView chart canvas recalculates its dimensions smoothly
  useEffect(() => {
    const fireResize = () => {
      try {
        window.dispatchEvent(new Event("resize"));
      } catch (e) {}
    };
    fireResize();
    const t1 = setTimeout(fireResize, 100);
    const t2 = setTimeout(fireResize, 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [layout]);

  // Load / initialize slot contracts
  const [slots, setSlots] = useState(() => {
    try {
      const cached = localStorage.getItem("options_multichart_slots");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length >= 4) {
          return parsed;
        }
      }
    } catch (e) {}

    // Initialize slot_0 with current selected contract if available
    const initialSlots = [...DEFAULT_OPTIONS_SLOTS];
    if (assetpair && currencycode) {
      initialSlots[0] = {
        ...initialSlots[0],
        currencycode,
        basecurrency: basecurrency || "USDT",
        assetpair,
        assetPairName: assetPairName || assetpair,
      };
    }
    return initialSlots;
  });

  // When external contract changes (e.g. from OptionLeftTable), update ONLY the active slot
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (assetpair) {
      setSlots((prev) => {
        const next = [...prev];
        const currentSlot = next[activeSlotIndex];
        const curAssetPair = assetpair;

        if (currentSlot?.assetpair !== curAssetPair) {
          next[activeSlotIndex] = {
            ...currentSlot,
            currencycode: currencycode || currentSlot?.currencycode || "BTC",
            basecurrency: basecurrency || currentSlot?.basecurrency || "USDT",
            assetpair: curAssetPair,
            assetPairName: assetPairName || curAssetPair,
          };
          try {
            localStorage.setItem("options_multichart_slots", JSON.stringify(next));
          } catch (e) {}
        }
        return next;
      });
    }
  }, [assetpair, assetPairName, currencycode, basecurrency, activeSlotIndex]);

  // Persist slots changes
  const updateSlot = (index, newProps) => {
    setSlots((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...newProps };
      try {
        localStorage.setItem("options_multichart_slots", JSON.stringify(next));
      } catch (e) {}
      return next;
    });

    // If updating the active slot, also push to OptionDataContenxt so order book syncs
    if (index === activeSlotIndex) {
      isInternalChangeRef.current = true;
      if (newProps.currencycode && setCurrencycode) setCurrencycode(newProps.currencycode);
      if (newProps.basecurrency && setBasecurrency) setBasecurrency(newProps.basecurrency);
      if (newProps.assetpair && setAssetPair) setAssetPair(newProps.assetpair);
      if (newProps.assetPairName && setAssetPairName) setAssetPairName(newProps.assetPairName);
    }
  };

  // User clicks a slot to activate it
  const handleActivateSlot = (index) => {
    setActiveSlotIndex(index);
    const targetSlot = slots[index];
    if (targetSlot) {
      isInternalChangeRef.current = true;
      if (targetSlot.currencycode && setCurrencycode) setCurrencycode(targetSlot.currencycode);
      if (targetSlot.basecurrency && setBasecurrency) setBasecurrency(targetSlot.basecurrency);
      if (targetSlot.assetpair && setAssetPair) setAssetPair(targetSlot.assetpair);
      if (targetSlot.assetPairName && setAssetPairName) setAssetPairName(targetSlot.assetPairName);
    }
  };

  // Determine which slots are visible based on current layout
  const visibleCount = useMemo(() => {
    switch (layout) {
      case "1":
        return 1;
      case "2-cols":
      case "2-rows":
        return 2;
      case "3-cols":
      case "3-rows":
      case "3-top-split":
      case "3-bottom-split":
        return 3;
      case "4-grid":
      case "4-cols":
      case "4-left-main":
      case "4-top-main":
        return 4;
      default:
        return 1;
    }
  }, [layout]);

  // Compute layout grid styles
  const { gridStyle, getSlotItemStyle } = useMemo(() => {
    const baseSlot = {
      width: "100%",
      minHeight: 0,
      minWidth: 0,
    };

    switch (layout) {
      case "1":
        return {
          gridStyle: {
            display: "block",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "2-cols":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "2-rows":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "3-cols":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "3-rows":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gridTemplateRows: "repeat(3, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "3-top-split":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 0 ? "1 / span 2" : undefined,
          }),
        };

      case "3-bottom-split":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr) minmax(0, 1.2fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 2 ? "1 / span 2" : undefined,
          }),
        };

      case "4-grid":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "4-cols":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };

      case "4-left-main":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.8fr) minmax(0, 1.2fr)",
            gridTemplateRows: "repeat(3, minmax(0, 1fr))",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 0 ? "1" : "2",
            gridRow: idx === 0 ? "1 / span 3" : undefined,
          }),
        };

      case "4-top-main":
        return {
          gridStyle: {
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: "4px",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: (idx) => ({
            ...baseSlot,
            gridColumn: idx === 0 ? "1 / span 3" : undefined,
          }),
        };

      default:
        return {
          gridStyle: {
            display: "block",
            width: "100%",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
          },
          getSlotItemStyle: () => baseSlot,
        };
    }
  }, [layout]);

  // Render single chart mode
  if (layout === "1") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <OptionTVChart
          currencycode={currencycode}
          basecurrency={basecurrency}
          assetpair={assetpair}
          assetPairName={assetPairName}
          instanceId="main"
          height="100%"
          showHeader={false}
        />
      </div>
    );
  }

  // Render multi-chart grid
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        padding: "4px",
        boxSizing: "border-box",
        backgroundColor: "var(--body-color-less, #181a20)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ ...gridStyle, height: "100%", width: "100%", minHeight: 0, minWidth: 0, boxSizing: "border-box" }}>
        {slots.slice(0, visibleCount).map((slot, idx) => {
          const isActive = idx === activeSlotIndex;
          const slotStyle = getSlotItemStyle(idx);

          return (
            <div
              key={slot.id || `slot_${idx}`}
              onClick={() => handleActivateSlot(idx)}
              style={{
                ...slotStyle,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
                minWidth: 0,
                borderRadius: "8px",
                border: isActive
                  ? `1.5px solid ${themeBtnColor}`
                  : "1px solid rgba(125, 125, 125, 0.2)",
                boxShadow: isActive
                  ? `0 0 10px ${alpha(themeBtnColor, 0.25)}`
                  : "none",
                backgroundColor: "var(--body-color, #1e2329)",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                position: "relative",
                boxSizing: "border-box",
              }}
            >
              <OptionTVChart
                currencycode={slot.currencycode}
                basecurrency={slot.basecurrency}
                assetpair={slot.assetpair}
                assetPairName={slot.assetPairName}
                instanceId={slot.id || `slot_${idx}`}
                height="100%"
                showHeader={true}
                showClose={idx > 0}
                isActive={isActive}
                btnColor={themeBtnColor}
                onActivate={() => handleActivateSlot(idx)}
                onClose={() => {
                  if (onLayoutChange) {
                    onLayoutChange("1");
                  }
                }}
                onSymbolChange={(newContract) => {
                  updateSlot(idx, {
                    currencycode: newContract.currencyCode,
                    basecurrency: newContract.baseCurrency,
                    assetpair: newContract.assetPair,
                    assetPairName: newContract.assetPairName,
                  });
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OptionMultiChartContainer;
